/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Public Plans Schemas & DTOs
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Public plan item schema exposed via edge API.
 */
export const PublicPlanItemSchema = z
  .object({
    id: z.string().openapi({ description: 'Plan identifier', example: 'plan_pro' }),
    name: z.string().openapi({ description: 'Display name of the tier', example: 'Professional' }),
    planType: z.enum(['subscription', 'token_pack']).openapi({ description: 'Type of plan', example: 'subscription' }),
    price: z.number().nonnegative().openapi({ description: 'Price in USD', example: 29 }),
    billingCycle: z.enum(['monthly', 'yearly', 'one_time']).openapi({ description: 'Billing frequency', example: 'monthly' }),
    creditsIncluded: z.number().nonnegative().openapi({ description: 'Credits allocated per billing cycle', example: 5000 }),
    features: z.array(z.string()).openapi({
      description: 'Checklist of features and capabilities',
      example: ['5,000 Edge credits / month', 'Access to all flagship models', 'BYOK unlocked'],
    }),
    stripePriceId: z.string().nullable().optional().openapi({ description: 'Stripe Price ID for checkout', example: 'price_pro_monthly' }),
    isFeatured: z.boolean().openapi({ description: 'Highlighted popular choice flag', example: true }),
    isActive: z.boolean().openapi({ description: 'Whether the plan is available for purchase', example: true }),
  })
  .openapi('PublicPlanItem');

export type PublicPlanItem = z.infer<typeof PublicPlanItemSchema>;

/**
 * Query filter parameters for public plans list.
 */
export const PublicPlansQuerySchema = z
  .object({
    planType: z.enum(['subscription', 'token_pack']).optional().openapi({ description: 'Filter by plan type', example: 'subscription' }),
    billingCycle: z.enum(['monthly', 'yearly', 'one_time']).optional().openapi({ description: 'Filter by billing cycle', example: 'monthly' }),
  })
  .openapi('PublicPlansQuery');

export type PublicPlansQuery = z.infer<typeof PublicPlansQuerySchema>;

/**
 * Admin plan item representation in backoffice.
 */
export const AdminPlanItemSchema = z
  .object({
    id: z.string().openapi({ description: 'Plan identifier', example: 'plan_pro' }),
    name: z.string().openapi({ description: 'Display name of the tier', example: 'Professional' }),
    planType: z.enum(['subscription', 'token_pack']).openapi({ description: 'Type of plan', example: 'subscription' }),
    price: z.number().nonnegative().openapi({ description: 'Price in USD', example: 29 }),
    billingCycle: z.enum(['monthly', 'yearly', 'one_time']).openapi({ description: 'Billing frequency', example: 'monthly' }),
    creditsIncluded: z.number().nonnegative().openapi({ description: 'Credits allocated per billing cycle', example: 5000 }),
    features: z.array(z.string()).default([]).openapi({
      description: 'Checklist of features and capabilities',
      example: ['5,000 Edge credits / month', 'Access to all flagship models', 'BYOK unlocked'],
    }),
    stripePriceId: z.string().nullable().optional().openapi({ description: 'Stripe Price ID for checkout', example: 'price_pro_monthly' }),
    isFeatured: z.boolean().openapi({ description: 'Highlighted popular choice flag', example: true }),
    isActive: z.boolean().openapi({ description: 'Whether the plan is available for purchase', example: true }),
  })
  .openapi('AdminPlanItem');

export type AdminPlanItem = z.infer<typeof AdminPlanItemSchema>;

/**
 * DTO Schema for creating a new plan in backoffice (Task 11.3.1).
 */
export const AdminPlanCreateSchema = z
  .object({
    name: z.string().min(1, 'Plan name is required').openapi({ description: 'Plan name', example: 'Pro Yearly' }),
    planType: z.enum(['subscription', 'token_pack']).default('subscription').openapi({ description: 'Plan type', example: 'subscription' }),
    price: z.number().min(0, 'Price must be non-negative').openapi({ description: 'Price in USD', example: 290 }),
    billingCycle: z.enum(['monthly', 'yearly', 'one_time']).default('monthly').openapi({ description: 'Billing frequency', example: 'yearly' }),
    creditsIncluded: z.number().min(0).default(0).openapi({ description: 'Credits allocated', example: 60000 }),
    features: z.array(z.string()).default([]).openapi({ description: 'Features list', example: ['60k credits/yr', 'Priority routing'] }),
    stripePriceId: z.string().nullable().optional().openapi({ description: 'Stripe Price ID' }),
    isActive: z.boolean().default(true).openapi({ description: 'Active status toggle' }),
    isFeatured: z.boolean().default(false).openapi({ description: 'Featured badge toggle' }),
  })
  .openapi('AdminPlanCreateRequest');

export type AdminPlanCreateInput = z.infer<typeof AdminPlanCreateSchema>;

/**
 * DTO Schema for updating an existing plan configuration (Task 11.3.1).
 */
export const AdminPlanUpdateSchema = z
  .object({
    name: z.string().min(1).optional().openapi({ description: 'Updated plan name' }),
    planType: z.enum(['subscription', 'token_pack']).optional().openapi({ description: 'Updated plan type' }),
    price: z.number().min(0).optional().openapi({ description: 'Updated price in USD' }),
    billingCycle: z.enum(['monthly', 'yearly', 'one_time']).optional().openapi({ description: 'Updated billing cycle' }),
    creditsIncluded: z.number().min(0).optional().openapi({ description: 'Updated credits quota' }),
    features: z.array(z.string()).optional().openapi({ description: 'Updated features list' }),
    stripePriceId: z.string().nullable().optional().openapi({ description: 'Updated Stripe Price ID' }),
    isActive: z.boolean().optional().openapi({ description: 'Toggle plan active status' }),
    isFeatured: z.boolean().optional().openapi({ description: 'Toggle featured status' }),
  })
  .openapi('AdminPlanUpdateRequest');

export type AdminPlanUpdateInput = z.infer<typeof AdminPlanUpdateSchema>;

/**
 * Query schema for backoffice plans list.
 */
export const AdminPlanQuerySchema = z
  .object({
    planType: z.enum(['subscription', 'token_pack', 'all']).optional().openapi({ description: 'Filter by plan type' }),
    billingCycle: z.enum(['monthly', 'yearly', 'one_time', 'all']).optional().openapi({ description: 'Filter by billing cycle' }),
    isActive: z.enum(['true', 'false', 'all']).optional().openapi({ description: 'Filter by active status' }),
    search: z.string().optional().openapi({ description: 'Search term for plan name' }),
  })
  .openapi('AdminPlanQuery');

export type AdminPlanQuery = z.infer<typeof AdminPlanQuerySchema>;
